import React, { type ButtonHTMLAttributes, type ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    children: ReactNode;
    variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'outline';
    size?: 'sm' | 'md' | 'lg';
    fullWidth?: boolean;
}

export const Button = ({
    children,
    variant = 'secondary',
    size = 'md',
    fullWidth = false,
    className = '',
    style,
    ...props
}: ButtonProps) => {

    const getVariantStyles = () => {
        switch (variant) {
            case 'primary':
                return {
                    background: 'var(--color-primary)',
                    color: 'white',
                    borderColor: 'var(--color-primary)',
                };
            case 'danger':
                return {
                    background: 'rgba(239, 68, 68, 0.2)',
                    color: 'var(--color-danger)',
                    borderColor: 'rgba(239, 68, 68, 0.3)',
                };
            case 'success':
                return {
                    background: 'rgba(34, 197, 94, 0.2)',
                    color: 'var(--color-success)',
                    borderColor: 'rgba(34, 197, 94, 0.3)',
                };
            case 'outline':
                return {
                    background: 'transparent',
                    color: 'var(--color-text-primary)',
                    borderColor: 'var(--color-border)',
                };
            default: // secondary
                return {
                    background: 'var(--color-bg-panel-hover)',
                    color: 'var(--color-text-primary)',
                    borderColor: 'var(--color-border)',
                };
        }
    };

    const getSizeStyles = () => {
        switch (size) {
            case 'sm':
                return { padding: '4px 10px', fontSize: '0.875rem' };
            case 'lg':
                return { padding: '12px 24px', fontSize: '1.125rem' };
            default:
                return { padding: '8px 16px', fontSize: '1rem' };
        }
    };

    const baseStyles: React.CSSProperties = {
        borderRadius: '8px',
        borderWidth: '1px',
        borderStyle: 'solid',
        cursor: props.disabled ? 'not-allowed' : 'pointer',
        opacity: props.disabled ? 0.5 : 1,
        transition: 'all 0.2s',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        justifyContent: 'center',
        fontWeight: 500,
        width: fullWidth ? '100%' : 'auto',
        ...getVariantStyles(),
        ...getSizeStyles(),
        ...style,
        boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    };

    const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)';
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 2px 0 rgb(0 0 0 / 0.05)';
    };

    return (
        <button
            className={className}
            style={baseStyles}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            {...props}
        >
            {children}
        </button>
    );
};
